import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import { Mail, Linkedin, CalendarIcon, Send, Phone, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SITE, ENQUIRY_TYPES } from "@/lib/data";

const initial = {
  name: "", email: "", organisation: "", phone: "",
  enquiry_type: "Book a Consultation", message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        preferred_date: date ? format(date, "PPP") : "",
      });
      toast.success("Thank you — your enquiry has been sent. Charmaine will be in touch shortly.");
      setForm(initial);
      setDate(null);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-navy text-cream pt-40 pb-24 grain">
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="overline mb-6">Contact</p>
            <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-cream">
              Let's start a conversation.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-cream/75 leading-relaxed">
              Whether you're establishing a new service, strengthening safeguarding or seeking
              strategic advice — we'd be glad to explore how we can partner with you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 grid gap-14 lg:grid-cols-12">
        {/* Details */}
        <div className="lg:col-span-4">
          <Reveal>
            <h2 className="font-serif text-2xl text-navy">Direct contact</h2>
            <div className="mt-8 space-y-5">
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-navy text-gold"><Mail size={18} /></span>
                <span className="text-navy/80 group-hover:text-navy">{SITE.email}</span>
              </a>
              <a href={SITE.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-4 group">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-navy text-gold"><Linkedin size={18} /></span>
                <span className="text-navy/80 group-hover:text-navy">Connect on LinkedIn</span>
              </a>
            </div>
            <div className="mt-10 rounded-3xl bg-cream border border-slate-200 p-7">
              <p className="overline mb-3">Consultation</p>
              <p className="text-navy/80 leading-relaxed">
                Select "Book a Consultation" and a preferred date, and we'll confirm a time that
                works for you.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="rounded-3xl border border-gold/40 bg-white p-12 text-center soft-shadow"
              >
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-teal text-cream">
                  <Check size={30} />
                </div>
                <h3 className="font-serif text-2xl text-navy">Thank you</h3>
                <p className="mt-3 text-slate-600">
                  Your enquiry has been received. Charmaine will be in touch shortly.
                </p>
                <Button onClick={() => setDone(false)} className="mt-8 rounded-full bg-navy text-cream" data-testid="send-another">
                  Send another enquiry
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="rounded-3xl border border-slate-200 bg-white p-8 md:p-10 soft-shadow">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" required data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@organisation.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="organisation">Organisation</Label>
                    <Input id="organisation" data-testid="input-organisation" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Your organisation" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input id="phone" data-testid="input-phone" className="pl-9" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" />
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>How can we help? *</Label>
                    <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                      <SelectTrigger data-testid="select-enquiry-type">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {ENQUIRY_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`enquiry-option-${t}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="date-trigger"
                          className="w-full justify-start text-left font-normal border-input"
                        >
                          <CalendarIcon size={16} className="mr-2 text-slate-400" />
                          {date ? format(date, "PPP") : <span className="text-slate-400">Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea id="message" required rows={5} data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a little about your organisation and what you're looking to achieve..." />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-enquiry"
                  className="mt-8 w-full rounded-full bg-navy hover:bg-navy-light text-cream h-12"
                >
                  {loading ? "Sending..." : (<>Send Enquiry <Send size={16} className="ml-2" /></>)}
                </Button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
